import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Confetti from "react-confetti";
import Shell from "@/components/app/Shell";
import api from "@/lib/api";
import { TRIBES, tribeForChild } from "@/lib/tribes";
import CompanionEncouragement from "@/components/app/CompanionEncouragement";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Play, ClipboardCheck, Award, Flag, Compass, Lock, Sparkles, Flame, FileText, Heart, BookMarked, BookOpen, Camera, Wind, TrendingUp, X } from "lucide-react";
import TribeEmblem from "@/components/app/TribeEmblem";
import { providerPdf } from "@/lib/pdf";
import { SYMPTOM_OPTIONS } from "@/lib/tribes";
import { useAuth } from "@/context/AuthContext";
import { previewStatus, PreviewBanner, PreviewEndedScreen, useUnlockModal } from "@/components/app/PreviewGate";

const SYMPTOM_RATING_COLOR = { better: "#4A7C59", same: "#C9A96E", worse: "#B45E5E" };
const SYMPTOM_RATING_LABEL = { better: "Better", same: "Same", worse: "Worse" };

// Streak milestones — first time streak.current hits one of these for this child,
// fire a confetti burst + celebratory toast. localStorage guards against re-firing
// on refresh. Order matters: highest first so a jump of >1 day still picks the right one.
const STREAK_MILESTONES = [30, 14, 7, 3];
const MILESTONE_COPY = {
  3: { title: "3-day streak", body: "Three days in a row — that's how a habit is born." },
  7: { title: "One week strong", body: "Seven days of practice. This is the rhythm that changes everything." },
  14: { title: "Two full weeks", body: "Halfway to a month. Chelcie would be proud." },
  30: { title: "30-day streak", body: "A whole month. You're not building a habit anymore — you have one." },
};
const milestoneStorageKey = (childId, days) => `btt.streak-milestone.${childId}.${days}`;
// Comeback celebration: fired once per rescue cycle after the parent logs a
// practice following a streak-rescue email. Keyed by streak_rescue_sent_for so
// a *future* rescue cycle still celebrates the next return.
const comebackStorageKey = (childId, sentFor) => `btt.streak-rescue-comeback.${childId}.${sentFor}`;

function SymptomTrack({ symptomKey, name, baseline, baselineAccent, checkins, testid }) {
  const rows = checkins
    .filter((c) => !c.is_draft)
    .sort((a, b) => (a.session_number || 0) - (b.session_number || 0))
    .map((c) => {
      const rec = (c.symptom_updates || {})[symptomKey];
      return { s: c.session_number, rating: rec ? rec.rating : null, detail: rec?.detail || "" };
    })
    .filter((r) => r.rating);

  return (
    <div className="bg-warm-white border border-edge rounded-2xl p-4" data-testid={testid}>
      <div className="text-sm font-semibold text-navy">{name}</div>
      {baseline && (
        <div className="text-xs text-ink-mid mt-1 italic line-clamp-2" data-testid={`${testid}-baseline`}>
          <span className="uppercase tracking-widest not-italic font-bold mr-1" style={{ color: baselineAccent || "#8B5E7A" }}>Baseline:</span>
          {baseline}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-xs text-ink-soft mt-3">No check-ins yet — first update will appear here.</div>
      ) : (
        <div className="mt-3 flex items-center gap-1.5" data-testid={`${testid}-track`}>
          {rows.map((r, i) => (
            <div
              key={i}
              title={`Session ${r.s} · ${SYMPTOM_RATING_LABEL[r.rating] || r.rating}${r.detail ? ` — ${r.detail}` : ""}`}
              className="flex-1 h-8 rounded-md flex items-center justify-center text-[10px] font-bold text-warm-white"
              style={{ background: SYMPTOM_RATING_COLOR[r.rating] || "#8B5E7A" }}
              data-testid={`${testid}-s${r.s}`}
            >
              S{r.s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProgressChart({ data, dataKey, color, accentTop, note, testid }) {
  const latest = data.length ? data[data.length - 1][dataKey.k] : null;
  const display = (latest === null || latest === undefined || Number.isNaN(latest)) ? "—" : `${latest}${dataKey.suffix || ""}`;
  return (
    <div className="bg-warm-white border border-edge rounded-2xl p-5 overflow-hidden" style={{ borderTop: accentTop ? `3px solid ${accentTop}` : undefined }} data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">{dataKey.label}</div>
      <div className="font-display text-2xl font-bold text-navy mt-1">{display}</div>
      {note && <div className="text-xs mt-1 italic" style={{ color: accentTop || "#8B5E7A" }}>{note}</div>}
      <div className="mt-3 h-24">
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
            <CartesianGrid stroke="#F2ECE6" vertical={false} />
            <XAxis dataKey="s" tick={{ fill: "#7A7A9A", fontSize: 10 }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: "#7A7A9A", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ background: "#FFFEF9", border: "1px solid #E0D8E8", borderRadius: 12 }} />
            <Line type="monotone" dataKey={dataKey.k} stroke={color} strokeWidth={3} dot={{ r: 4, fill: color }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function ChildHome() {
  const { childId } = useParams();
  const { user } = useAuth();
  const unlock = useUnlockModal();
  const [child, setChild] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [certs, setCerts] = useState([]);
  const [streak, setStreak] = useState({ current: 0, longest: 0, total: 0 });
  const [roadblocks, setRoadblocks] = useState([]);
  const [practiceUnlocked, setPracticeUnlocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const [celebrateDays, setCelebrateDays] = useState(null); // int | null — active milestone burst
  const [celebrateComeback, setCelebrateComeback] = useState(false); // "welcome back" after a rescue email
  const [confettiSize, setConfettiSize] = useState({ w: 0, h: 0 });
  const nav = useNavigate();

  // Track window size for the confetti canvas.
  useEffect(() => {
    const upd = () => setConfettiSize({ w: window.innerWidth, h: window.innerHeight });
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  // Detect the first time this child crosses a milestone. Guarded by localStorage
  // so a page reload the same day doesn't re-trigger. Auto-dismiss after 6s.
  useEffect(() => {
    if (!childId || !streak.current) return;
    const hit = STREAK_MILESTONES.find((m) => streak.current >= m && !localStorage.getItem(milestoneStorageKey(childId, m)));
    if (!hit) return;
    localStorage.setItem(milestoneStorageKey(childId, hit), new Date().toISOString());
    setCelebrateDays(hit);
    const t = setTimeout(() => setCelebrateDays(null), 6000);
    return () => clearTimeout(t);
  }, [childId, streak.current]);

  // Detect the "welcome back" moment: the backend stamps streak_rescue_comeback_at
  // on the child the first time a practice lands after a rescue email. We celebrate
  // once per rescue cycle (keyed by streak_rescue_sent_for). Auto-dismiss after 7s.
  useEffect(() => {
    if (!child) return;
    const sentFor = child.streak_rescue_sent_for;
    const comebackAt = child.streak_rescue_comeback_at;
    if (!sentFor || !comebackAt) return;
    if (localStorage.getItem(comebackStorageKey(childId, sentFor))) return;
    localStorage.setItem(comebackStorageKey(childId, sentFor), new Date().toISOString());
    setCelebrateComeback(true);
    const t = setTimeout(() => setCelebrateComeback(false), 7000);
    return () => clearTimeout(t);
  }, [child, childId]);

  useEffect(() => {
    Promise.all([
      api.get(`/children/${childId}`),
      api.get(`/checkins/${childId}`),
      api.get(`/certificates/${childId}`),
      api.get(`/streak/${childId}`),
      api.get(`/roadblocks/${childId}`),
    ]).then(async ([c, ck, cf, st, rb]) => {
      setChild(c.data); setCheckins(ck.data); setCerts(cf.data); setStreak(st.data); setRoadblocks(rb.data);
      // Only fetch learn-progress for the current session — used to enable Practice-Today button.
      try {
        const lp = await api.get(`/sessions/${c.data.current_session}/learn-progress?child_id=${childId}`);
        setPracticeUnlocked(!!lp.data?.complete);
      } catch (_) { setPracticeUnlocked(false); }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [childId]);

  const downloadProvider = () => {
    const doc = providerPdf(child, checkins, roadblocks, streak);
    doc.save(`provider-summary-${child.name}.pdf`);
  };

  if (loading || !child) return <Shell><div className="text-ink-mid">Loading…</div></Shell>;

  // Preview: hard lockout once the 4-day window has closed.
  const preview = previewStatus(user);
  if (preview.isExpired) return <PreviewEndedScreen />;

  const tribe = tribeForChild(child);
  const unlocksAt = new Date(child.session_unlocks_at);
  const now = new Date();
  const locked = unlocksAt > now && child.current_session > 1;
  const daysLeft = Math.max(0, Math.ceil((unlocksAt - now) / (1000 * 60 * 60 * 24)));

  const num = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : 0);
  const congestionFlags = checkins.filter((c) => (c.congestion || {}).blocking_nose === "yes").length;
  const chartData = checkins.map((c) => {
    const b = c.breathing || {};
    const l = c.lips || {};
    const t = c.tongue || {};
    const s = c.swallow || {};
    return {
      s: `S${c.session_number}`,
      nasal: Math.round((num(b.nasal_day_pct) + num(b.nasal_night_pct)) / 2),
      lipseal: num(l.sealed_pct),
      tongue: Math.round((num(t.tip_spot_pct) + num(t.mid_palate_pct) + num(t.back_palate_pct)) / 3),
      swallow: s.proper_easy === "yes" ? 100 : s.proper_easy === "somewhat" ? 50 : 0,
    };
  });

  const celebrateCopy = celebrateDays ? MILESTONE_COPY[celebrateDays] : null;
  const streakLen = child.streak_rescue_last_length || 0;

  return (
    <Shell>
      {(celebrateDays || celebrateComeback) && (
        <div className="fixed inset-0 pointer-events-none z-40" aria-hidden="true">
          <Confetti
            width={confettiSize.w}
            height={confettiSize.h}
            colors={celebrateComeback ? ["#FF3CB4", "#C9A96E", tribe.accent, "#4A7C59", "#FFFEF9"] : [tribe.accent, "#C9A96E", "#FF3CB4", "#2C2D5B", "#FFFEF9"]}
            numberOfPieces={celebrateComeback ? 200 : 260}
            recycle={false}
            gravity={0.22}
            tweenDuration={celebrateComeback ? 4800 : 5200}
          />
        </div>
      )}
      {celebrateDays && (
        <div
          data-testid={`streak-milestone-toast-${celebrateDays}`}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[92%] bg-navy text-warm-white rounded-3xl shadow-2xl px-5 py-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-4"
        >
          <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: tribe.spark }}>
            <Flame size={20} style={{ color: tribe.spark_ink }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: tribe.spark }}>Milestone unlocked</div>
            <div className="font-display text-lg font-bold mt-0.5">{celebrateCopy.title}</div>
            <div className="text-xs text-warm-white/80 mt-1 leading-relaxed">{celebrateCopy.body}</div>
          </div>
          <button
            type="button"
            onClick={() => setCelebrateDays(null)}
            data-testid="streak-milestone-dismiss"
            className="text-warm-white/60 hover:text-warm-white shrink-0"
            aria-label="Dismiss milestone celebration"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {celebrateComeback && (
        <div
          data-testid="streak-rescue-comeback-toast"
          style={{ background: tribe.accent }}
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[92%] text-warm-white rounded-3xl shadow-2xl px-5 py-4 flex items-start gap-3 animate-in fade-in slide-in-from-top-4"
        >
          <div className="w-11 h-11 rounded-full bg-warm-white flex items-center justify-center shrink-0">
            <Heart size={20} style={{ color: tribe.accent }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: tribe.spark_ink === "#FFFEF9" ? tribe.subtle : tribe.spark }}>Welcome back</div>
            <div className="font-display text-lg font-bold mt-0.5">You came back. That's the whole game.</div>
            <div className="text-xs text-warm-white/85 mt-1 leading-relaxed">
              {streakLen >= 3
                ? `Your ${streakLen}-day streak got interrupted — this practice is day one of the next one. `
                : "One practice lands you right back in the rhythm. "}
              Progress, not perfection.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCelebrateComeback(false)}
            data-testid="streak-rescue-comeback-dismiss"
            className="text-warm-white/70 hover:text-warm-white shrink-0"
            aria-label="Dismiss comeback celebration"
          >
            <X size={16} />
          </button>
        </div>
      )}
      <PreviewBanner user={user} />
      {showEncouragement && (
        <CompanionEncouragement child={child} onClose={() => setShowEncouragement(false)} />
      )}
      {child.tribe === "thrive_story" && !child.story_assets_ready && (
        <div className="mb-6 rounded-2xl border-2 p-5 flex items-start gap-3" style={{ background: "linear-gradient(135deg,#FBF5FA 0%,#FFFEF9 100%)", borderColor: "#8B5E7A" }} data-testid="thrive-story-pending">
          <Sparkles size={20} className="shrink-0 mt-0.5" style={{ color: "#8B5E7A" }} />
          <div>
            <div className="font-display text-lg font-bold text-navy">Your Thrive Story is being created</div>
            <p className="text-sm text-ink-mid mt-1 leading-relaxed">
              Our team is crafting {child.name}'s personalized world right now — their favorite color, their adventure companion, and twelve one-of-a-kind certificates. In the meantime, you can keep practicing and taking check-ins. The theme will light up the moment it's ready.
            </p>
          </div>
        </div>
      )}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4">
          <TribeEmblem tribeKey={child.tribe} child={child} size={56} testid={`child-emblem-${childId}`} />
          <div>
            {(child.is_self || child.tribe !== "neutral") && (
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.subtle }}>{child.is_self ? "Self-enrolled" : tribe.name}</div>
            )}
            <h1 className="font-display text-4xl font-bold text-navy mt-1">{child.is_self ? "Your journey" : `${child.name}'s journey`}</h1>
            <p className="text-ink-mid mt-1">Session {child.current_session} of 12 — progress, not perfection.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {preview.isPreview ? (
            <button data-testid="open-session-btn-locked" onClick={() => unlock.open("Sessions unlock the moment you enroll — your tribe, symptoms, and progress are already saved.")} style={{ background: `${tribe.accent}99`, color: tribe.panel_ink }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer">
              <Lock size={14} /> Session {child.current_session}
            </button>
          ) : (
            <Link to={`/child/${childId}/session/${child.current_session}`} data-testid="open-session-btn" style={{ background: tribe.accent, color: tribe.panel_ink }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
              <Play size={14} /> Session {child.current_session}
            </Link>
          )}
          {preview.isPreview ? (
            <button data-testid="new-checkin-btn-locked" onClick={() => unlock.open("The Myo Check-In opens when you enroll. It's the tracking heart of the program.")} style={{ background: `${tribe.subtle}99`, color: tribe.ink }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold cursor-pointer">
              <Lock size={14} /> Check-in
            </button>
          ) : (
            <Link to={`/child/${childId}/checkin`} data-testid="new-checkin-btn" style={{ background: tribe.subtle, color: tribe.ink }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
              <ClipboardCheck size={14} /> Check-in
            </Link>
          )}
          {!preview.isPreview && (
            practiceUnlocked ? (
              <Link
                to={`/child/${childId}/practice`}
                data-testid="practice-today-btn"
                style={{ background: tribe.accent, color: tribe.panel_ink }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <Flame size={14} /> Practice today's session
                {streak.current > 0 && (
                  <span
                    data-testid="practice-today-streak-badge"
                    style={{ background: tribe.spark, color: tribe.spark_ink }}
                    className="inline-flex items-center gap-1 -mr-1 pl-2 pr-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
                  >
                    <Flame size={10} /> Day {streak.current}
                  </span>
                )}
              </Link>
            ) : (
              <Link
                to={`/child/${childId}/session/${child.current_session}?skip=learn`}
                data-testid="practice-today-btn-locked"
                title="Finish Learn the Exercises for this session first — this button opens once every exercise is viewed."
                style={{ background: `${tribe.accent}25`, color: tribe.accent }}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                <Lock size={14} /> Practice today's session
                {streak.current > 0 && (
                  <span
                    data-testid="practice-today-streak-badge-locked"
                    style={{ background: `${tribe.spark}40`, color: tribe.accent }}
                    className="inline-flex items-center gap-1 -mr-1 pl-2 pr-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
                  >
                    <Flame size={10} /> Day {streak.current}
                  </span>
                )}
              </Link>
            )
          )}
          {child.current_session >= 11 && (
            <Link to={`/child/${childId}/triage`} data-testid="triage-btn" style={{ background: tribe.panel_soft, color: tribe.accent }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
              <Compass size={14} /> Triage
            </Link>
          )}
          {/* Adventure companion encouragement — only renders for published Thrive Story
              kids who have at least one encouragement configured. Positioned inline with
              Open Session + Check-in so it's visible in a tough moment but doesn't push
              other CTAs around. */}
          {child.tribe === "thrive_story" && child.story_assets_ready && (child.encouragements?.length > 0) && (
            <button
              onClick={() => setShowEncouragement(true)}
              data-testid="encouragement-btn"
              style={{ background: `${tribe.accent}18`, color: tribe.accent, borderColor: `${tribe.accent}55` }}
              className="inline-flex items-center gap-2 border px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <Heart size={14} /> Get encouragement from {child.companion_name || "your companion"}
            </button>
          )}
          {/* Full Thrive World PDF — only renders when admin has uploaded the family's
              personalized 28-page story. Opens in a new tab so parents can view, download
              or print without leaving the dashboard. */}
          {child.tribe === "thrive_story" && child.story_assets_ready && child.thrive_world_pdf_path && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/public-files/${child.thrive_world_pdf_path}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thrive-world-pdf-btn"
              style={{ background: `${tribe.accent}18`, color: tribe.accent, borderColor: `${tribe.accent}55` }}
              className="inline-flex items-center gap-2 border px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <BookOpen size={14} /> View Full Thrive World
            </a>
          )}
          <Link to={`/child/${childId}/library`} data-testid="session-library-btn" style={{ color: tribe.accent, borderColor: `${tribe.accent}55` }} className="inline-flex items-center gap-2 bg-warm-white border px-5 py-2.5 rounded-full text-sm font-semibold hover:border-current transition-colors">
            <BookMarked size={14} /> Session library
          </Link>
          <Link to={`/child/${childId}/timeline`} data-testid="family-timeline-btn" style={{ color: tribe.accent, borderColor: `${tribe.accent}55` }} className="inline-flex items-center gap-2 bg-warm-white border px-5 py-2.5 rounded-full text-sm font-semibold hover:border-current transition-colors">
            <Sparkles size={14} /> Timeline
          </Link>
          <Link to={`/child/${childId}/gallery`} data-testid="photo-gallery-btn" style={{ color: tribe.accent, borderColor: `${tribe.accent}55` }} className="inline-flex items-center gap-2 bg-warm-white border px-5 py-2.5 rounded-full text-sm font-semibold hover:border-current transition-colors">
            <Camera size={14} /> Photo gallery
          </Link>
          <button onClick={downloadProvider} data-testid="provider-summary-btn" style={{ color: tribe.accent, borderColor: `${tribe.accent}55` }} className="inline-flex items-center gap-2 bg-warm-white border px-5 py-2.5 rounded-full text-sm font-semibold hover:border-current transition-colors">
            <FileText size={14} /> Provider summary
          </button>
          <Link to="/fell-off" data-testid="fell-off-btn" style={{ background: tribe.panel_soft, color: tribe.accent }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity">
            <Heart size={14} /> Fell off track?
          </Link>
        </div>
      </div>

      {(streak.current > 0 || streak.longest > 1) && (
        <div className="mb-6 grid grid-cols-3 gap-4" data-testid="streak-panel">
          <div className="bg-warm-white border border-edge rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${tribe.accent}22` }}><Flame size={18} style={{ color: tribe.accent }} /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Current streak</div>
              <div className="font-display text-2xl font-bold text-navy" data-testid="streak-current">{streak.current} day{streak.current === 1 ? "" : "s"}</div>
            </div>
          </div>
          <div className="bg-warm-white border border-edge rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${tribe.subtle}33` }}><Sparkles size={18} style={{ color: tribe.accent }} /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Longest streak</div>
              <div className="font-display text-2xl font-bold text-navy">{streak.longest} day{streak.longest === 1 ? "" : "s"}</div>
            </div>
          </div>
          <div className="bg-warm-white border border-edge rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${tribe.accent}22` }}><Award size={18} style={{ color: tribe.accent }} /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Total practices</div>
              <div className="font-display text-2xl font-bold text-navy">{streak.total}</div>
            </div>
          </div>
        </div>
      )}

      {locked && (
        <div className="mb-6 rounded-2xl p-4 flex items-center gap-3 border" style={{ background: `${tribe.accent}12`, borderColor: `${tribe.accent}40` }}>
          <Lock size={16} style={{ color: tribe.accent }} />
          <div className="text-sm text-ink-mid">Next session unlocks in <b className="text-navy">{daysLeft} day{daysLeft !== 1 ? "s" : ""}</b>. Keep practicing — <span className="italic">progress, not perfection.</span></div>
        </div>
      )}

      {congestionFlags > 3 && (
        <div className="mb-6 rounded-2xl p-4 flex items-start gap-3 border" style={{ background: tribe.panel_soft, borderColor: tribe.accent }} data-testid="congestion-recurring-alert">
          <Wind size={18} className="mt-0.5" style={{ color: tribe.accent }} />
          <div className="flex-1">
            <div className="font-semibold text-navy">Congestion flagged <span data-testid="congestion-flag-count">{congestionFlags}</span> times</div>
            <div className="text-sm text-ink-mid mt-1">This has come up across multiple check-ins — worth a closer look. Try the Nasal &amp; Sleep Hygiene module, and if it keeps recurring, consider a provider visit.</div>
            <Link to="/learn?open=nasal_sleep_hygiene" data-testid="congestion-recurring-learn" style={{ color: tribe.accent }} className="mt-2 inline-flex items-center gap-2 text-sm underline font-semibold">
              Open Nasal &amp; Sleep Hygiene module
            </Link>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <ProgressChart testid="progress-track-nasal" data={chartData} dataKey={{ k: "nasal", label: "Nasal Breathing", suffix: "%" }} color={tribe.accent} accentTop={tribe.accent} />
        <ProgressChart testid="progress-track-lipseal" data={chartData} dataKey={{ k: "lipseal", label: "Lip Seal", suffix: "%" }} color={tribe.accent} accentTop={tribe.accent} note={child.appliance_in_use ? "May be related to your child's appliance/expander" : null} />
        <ProgressChart testid="progress-track-tongue" data={chartData} dataKey={{ k: "tongue", label: "Tongue Posture", suffix: "%" }} color={tribe.accent} accentTop={tribe.accent} note={child.appliance_in_use ? "May be related to your child's appliance/expander" : null} />
        <ProgressChart testid="progress-track-swallow" data={chartData} dataKey={{ k: "swallow", label: "Swallow Pattern", suffix: "" }} color={tribe.accent} accentTop={tribe.accent} />
      </div>

      {(() => {
        const dismissed = child.dismissed_symptoms || [];
        const trackedSymptoms = (child.top_symptoms || []).filter((k) => !dismissed.includes(k));
        if (trackedSymptoms.length === 0) return null;
        const baselines = child.symptom_baselines || {};
        return (
          <div className="mb-8" data-testid="symptom-timeline">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={14} style={{ color: tribe.accent }} />
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.accent }}>Symptom timeline</div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {trackedSymptoms.map((k) => {
                const opt = SYMPTOM_OPTIONS.find((s) => s.key === k);
                return (
                  <SymptomTrack
                    key={k}
                    symptomKey={k}
                    name={opt ? opt.name : k}
                    baseline={baselines[k]}
                    baselineAccent={tribe.accent}
                    checkins={checkins}
                    testid={`symptom-track-${k}`}
                  />
                );
              })}
            </div>
            {dismissed.length > 0 && (
              <div className="mt-3 text-xs text-ink-mid" data-testid="symptom-timeline-dismissed">
                <Sparkles size={11} className="inline mr-1" style={{ color: tribe.accent }} />
                No longer tracking: {dismissed.map((k) => (SYMPTOM_OPTIONS.find((s) => s.key === k)?.name || k)).join(", ")}
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-warm-white border border-edge rounded-3xl p-6">
          <h2 className="font-display text-xl font-bold text-navy">Sessions</h2>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => {
              const done = n < child.current_session;
              const current = n === child.current_session;
              const currentLocked = current && locked;
              const gated = n > child.current_session || currentLocked;
              const tileStyle = current && !currentLocked
                ? { background: tribe.subtle, borderColor: tribe.subtle, color: tribe.ink }
                : done
                ? { background: tribe.accent, borderColor: tribe.accent, color: tribe.panel_ink }
                : {};
              const tileFallback = !(current && !currentLocked) && !done ? "bg-cream border-edge text-ink-soft" : "";
              return (
                <Link key={n} to={gated ? "#" : `/child/${childId}/session/${n}`} data-testid={`session-tile-${n}`} aria-disabled={gated} onClick={(e) => gated && e.preventDefault()} style={tileStyle} className={`aspect-square rounded-2xl border p-3 flex flex-col justify-between transition-all ${gated ? "opacity-60 cursor-not-allowed " : ""}${tileFallback}`}>
                  <div className="text-xs uppercase tracking-widest font-bold opacity-70">Session</div>
                  <div className="font-display text-3xl font-bold">{n}</div>
                  <div className="text-xs">{done ? "Complete" : current ? "In progress" : "Locked"}</div>
                </Link>
              );
            })}
          </div>
        </div>
        <div className="bg-warm-white border border-edge rounded-3xl p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-navy">Certificates</h2>
            <Sparkles size={16} style={{ color: tribe.accent }} />
          </div>
          <div className="mt-3 space-y-2">
            {certs.length === 0 && <div className="text-sm text-ink-soft">Earn your first by completing session 1.</div>}
            {certs.map((c) => (
              <Link key={c.id} to={`/child/${childId}/certificate/${c.session_number}`} style={{ borderColor: `${tribe.accent}30` }} className="block px-4 py-3 rounded-xl border hover:border-current transition" data-testid={`cert-link-${c.session_number}`}>
                <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Session {c.session_number}</div>
                <div className="text-sm text-navy font-semibold">{new Date(c.date).toLocaleDateString()}</div>
              </Link>
            ))}
            {child.current_session > 12 && (
              <Link to={`/child/${childId}/graduation`} data-testid="graduation-link" style={{ background: tribe.accent, color: tribe.panel_ink }} className="block mt-3 px-4 py-3 rounded-xl text-sm font-semibold text-center">
                🎓 View Graduation
              </Link>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
